import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { PublishedItemStatus } from '@tempo-io/roadmap-dto';
import { usePublishedContext } from '@/components/views/published/PublishedContext';
import { usePublishedSnapshotQuery } from '@/components/views/published/usePublishedSnapshotQuery';
import type { ItemStatus } from '@/lib/api/types/itemStatus';
import { getItemStatusesQueryOptions } from '@/lib/queries/common/useGetItemStatusesQuery';
import { commonQueryKeys } from '@/lib/query-keys/common';

const VALID_SCHEDULING_TYPES = new Set(['TO_DO', 'IN_PROGRESS', 'DONE']);

function mapPublishedStatus(s: PublishedItemStatus): ItemStatus {
  return {
    id: s.id,
    name: s.name,
    color: s.color,
    default: false,
    archivedAt: null,
    schedulingType: VALID_SCHEDULING_TYPES.has(s.type)
      ? (s.type as ItemStatus['schedulingType'])
      : 'TO_DO',
    priority: '',
    createdAt: '',
    updatedAt: '',
  };
}

export const useItemStatusesQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemStatus[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const statusesQuery = useQuery({
    ...getItemStatusesQueryOptions(),
    ...options,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = usePublishedSnapshotQuery({
    baseKey: commonQueryKeys.itemStatuses.all(),
    getSnapshot: () => publishedContext?.itemStatuses.map(mapPublishedStatus),
  });

  return isPublished ? publishedQuery : statusesQuery;
};
